import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

/**
 * Componente reutilizable para captura de documentos con cámara del
 * dispositivo. Replica la lógica del `OcrDocumentStepComponent` del
 * onboarding para que la validación server-side funcione igual (Textract
 * y Rekognition esperan las mismas dimensiones y compresión).
 *
 * No hace OCR ni subida al backend — sólo captura y devuelve el
 * `base64` procesado vía `(captured)` para que el componente padre
 * decida qué hacer con la imagen.
 *
 * UX:
 *   1. Botón/trigger externo abre el modal (`isOpen = true`).
 *   2. Se inicializa la cámara (`getUserMedia`) con fallback highEnd →
 *      midRange → lowEnd.
 *   3. Frame overlay guía al usuario (mismas esquinas que el onboarding).
 *   4. Al tocar "Tomar foto" se captura el ÁREA DEL FRAME (crop al
 *      aspect ratio del documento), se comprime y se muestra en preview.
 *   5. Usuario puede rotar / retomar / confirmar.
 *   6. Al confirmar, se emite `(captured)` con el base64.
 */

/** Configuración de captura alineada con `OcrDocumentStepComponent`. */
const CAPTURE_CONFIG = {
  targetWidth: 1920,
  minWidth: 1280,
  maxWidth: 2400,
  quality: 0.85,
  minQuality: 0.5,
  qualityStep: 0.05,
  maxFileSizeMB: 4.5,
  targetFileSizeMB: 2,
  format: 'image/jpeg' as const,
  documentAspectRatio: 1.586,
};

/**
 * Configs de cámara por gama de dispositivo — se prueban en orden. Si
 * `highEnd` falla (dispositivo viejo), cae a `midRange` y finalmente
 * `lowEnd`. Se usa SOLO para documentos.
 */
const CAMERA_CONFIGS = {
  highEnd: {
    width: { ideal: 1920, min: 1280 },
    height: { ideal: 1080, min: 720 },
  },
  midRange: {
    width: { ideal: 1280, min: 640 },
    height: { ideal: 720, min: 480 },
  },
  lowEnd: {
    width: { ideal: 640 },
    height: { ideal: 480 },
  },
};

/**
 * Config de cámara para selfie — IDÉNTICA a la del onboarding
 * (`FaceCaptureStepComponent.SELFIE_CAMERA_CONFIG`). Un solo intento,
 * sin fallback: si el dispositivo no puede negociar estos constraints
 * el navegador ajusta hacia abajo dentro del rango `min`.
 *
 * Motivo: Rekognition tira `Request has invalid parameters` cuando las
 * dimensiones de la selfie difieren mucho de las que el onboarding
 * validó — el flujo de actualización tiene que producir imágenes con
 * la misma geometría para que la comparación facial sea consistente.
 */
const SELFIE_CAMERA_CONFIG = {
  width: { ideal: 1280, min: 640 },
  height: { ideal: 720, min: 480 },
};

@Component({
  selector: 'app-document-camera-capture',
  templateUrl: './document-camera-capture.component.html',
  styleUrls: ['./document-camera-capture.component.sass'],
})
export class DocumentCameraCaptureComponent implements OnDestroy {
  /**
   * Título del modal (ej. "Foto frontal del documento"). El padre lo
   * pasa según el `fieldKey` que está capturando.
   */
  @Input() title: string = 'Captura de documento';

  /** Texto guía debajo del título. */
  @Input() subtitle: string = 'Alineá el documento dentro del marco.';

  /**
   * Aspect ratio del frame overlay. Por defecto el de una cédula
   * (1.586). Si en el futuro se capturan otros documentos (pasaporte
   * 1.42, selfie 1:1, etc.) el padre puede sobreescribirlo.
   */
  @Input() frameAspectRatio: number = CAPTURE_CONFIG.documentAspectRatio;

  /**
   * Forma del frame overlay. `document` = rectángulo con 4 esquinas
   * (default, cédulas / pasaportes). `face` = óvalo tipo retrato — se
   * usa cuando lo que se captura es un rostro (selfie): el borde
   * rectangular con esquinas confundía a los clientes, que creían
   * tener que fotografiar SOLO el documento.
   */
  @Input() frameShape: 'document' | 'face' = 'document';

  /**
   * Cámara preferida al abrir. Para selfie con documento conviene la
   * frontal (`user`), para documentos la trasera (`environment`). El
   * usuario luego puede alternar con el botón "Cambiar cámara" si el
   * dispositivo tiene más de una.
   */
  @Input() preferredFacing: 'environment' | 'user' = 'environment';

  /**
   * `true` cuando el modal está abierto. Controlado por el padre para
   * poder abrirlo desde botones distintos ("Tomar foto", "Retomar").
   */
  @Input() set isOpen(value: boolean) {
    this._isOpen = value;
    if (value) {
      this.openCamera();
    } else {
      this.cleanup();
    }
  }
  get isOpen(): boolean {
    return this._isOpen;
  }
  private _isOpen: boolean = false;

  /** Emitido cuando el cliente confirma la imagen. Base64 JPEG. */
  @Output() captured = new EventEmitter<string>();

  /** Emitido cuando se cierra el modal sin capturar (cancelar). */
  @Output() closed = new EventEmitter<void>();

  /** Error de acceso a la cámara (permisos, sin cámara, etc.). */
  @Output() cameraError = new EventEmitter<string>();

  @ViewChild('video') videoElement?: ElementRef<HTMLVideoElement>;

  loading: boolean = false;
  isReadyToCapture: boolean = false;
  currentImage: string = '';
  rotationAngle: number = 0;

  /**
   * Cámara actualmente en uso. Arranca en `preferredFacing`; el usuario
   * puede alternar con el botón "Cambiar cámara" (`toggleCamera()`).
   */
  currentFacing: 'environment' | 'user' = 'environment';

  /**
   * `true` cuando el dispositivo reporta más de una cámara — el botón
   * de cambio sólo tiene sentido en ese caso.
   */
  hasMultipleCameras: boolean = false;

  private stream: MediaStream | null = null;

  ngOnDestroy(): void {
    this.cleanup();
  }

  /** Cerrar con ESC. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this._isOpen) this.close();
  }

  // ── Apertura / cierre ──────────────────────────────────────────────

  private async openCamera(resetFacing: boolean = true): Promise<void> {
    this.loading = true;
    this.isReadyToCapture = false;
    this.currentImage = '';
    this.rotationAngle = 0;
    if (resetFacing) {
      this.currentFacing = this.preferredFacing;
    }

    // Damos un tick al DOM para que renderice el <video> antes de
    // intentar bindear el srcObject.
    setTimeout(async () => {
      if (this.videoElement) {
        await this.initCamera();
        await this.detectAvailableCameras();
      }
      this.loading = false;
    }, 200);
  }

  /**
   * Enumera los dispositivos de video disponibles para saber si vale
   * la pena mostrar el botón "Cambiar cámara". Solo funciona tras
   * `getUserMedia` (los labels quedan vacíos sin permisos otorgados).
   */
  private async detectAvailableCameras(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((d) => d.kind === 'videoinput');
      this.hasMultipleCameras = cameras.length > 1;
    } catch {
      this.hasMultipleCameras = false;
    }
  }

  /**
   * Alterna entre cámara frontal y trasera. Detiene el stream actual y
   * pide uno nuevo con el `facingMode` opuesto. Si el navegador ignora
   * el `facingMode` (algunos desktops), volvemos al stream original.
   */
  async toggleCamera(): Promise<void> {
    if (!this.hasMultipleCameras) return;
    if (this.currentImage) return; // ya capturó, sin sentido cambiar
    this.currentFacing = this.currentFacing === 'environment' ? 'user' : 'environment';
    this.loading = true;
    this.isReadyToCapture = false;
    this.stopStream();
    try {
      await this.initCamera();
    } finally {
      this.loading = false;
    }
  }

  close(): void {
    this._isOpen = false;
    this.cleanup();
    this.closed.emit();
  }

  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    const video = this.videoElement?.nativeElement;
    if (video) {
      video.srcObject = null;
      try {
        video.load();
      } catch {
        // Ignore — algunos browsers tiran si el video no cargó.
      }
    }
  }

  // ── Inicialización de cámara ───────────────────────────────────────

  private async initCamera(): Promise<void> {
    const video = this.videoElement!.nativeElement;

    // Selfies: un solo intento con la config del onboarding — sin
    // fallback multi-gama. Rekognition tiraba `invalid parameters` por
    // diferencias de geometría vs onboarding; alineamos el negocio.
    if (this.frameShape === 'face') {
      try {
        const constraints = await this.buildSelfieConstraints();
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        video.srcObject = this.stream;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('autoplay', 'true');
        video.muted = true;
        await video.play();
        await this.applyOptimalSettings(this.stream);
        this.isReadyToCapture = true;
        return;
      } catch {
        this.cameraError.emit(
          'No se pudo acceder a la cámara. Verificá los permisos en tu navegador.',
        );
        this.close();
        return;
      }
    }

    // Documentos: fallback highEnd → midRange → lowEnd para máxima
    // resolución posible en cada dispositivo.
    const attempts = ['highEnd', 'midRange', 'lowEnd'] as const;

    for (const configType of attempts) {
      try {
        const constraints = await this.buildConstraints(configType);
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        video.srcObject = this.stream;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('autoplay', 'true');
        video.muted = true;
        await video.play();
        await this.applyOptimalSettings(this.stream);
        this.isReadyToCapture = true;
        return;
      } catch {
        // continuar con siguiente config
      }
    }

    this.cameraError.emit(
      'No se pudo acceder a la cámara. Verificá los permisos en tu navegador.',
    );
    this.close();
  }

  /**
   * Constraints para selfie — un solo intento, misma config que el
   * onboarding (`SELFIE_CAMERA_CONFIG`). Sin fallback multi-gama.
   * Respeta `currentFacing` para el botón "Cambiar cámara".
   */
  private async buildSelfieConstraints(): Promise<MediaStreamConstraints> {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;

    // iOS necesita un primer getUserMedia para poder enumerar
    // dispositivos con label — mismo warm-up que en el flujo de docs.
    if (isIOS) {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ video: true });
        tmp.getTracks().forEach((t) => t.stop());
      } catch {
        // Ignoramos — el getUserMedia real de abajo maneja el error.
      }
    }

    return {
      video: {
        ...SELFIE_CAMERA_CONFIG,
        facingMode: this.currentFacing,
      },
      audio: false,
    };
  }

  private async buildConstraints(
    configType: 'highEnd' | 'midRange' | 'lowEnd',
  ): Promise<MediaStreamConstraints> {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    const isAndroid = /Android/i.test(ua);
    const isMobile = isIOS || isAndroid;

    // iOS necesita permisos primero para poder enumerar dispositivos.
    if (isIOS && configType === 'highEnd') {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ video: true });
        tmp.getTracks().forEach((t) => t.stop());
      } catch {
        // Ignoramos y seguimos con el intento normal.
      }
    }

    const cfg = CAMERA_CONFIGS[configType];
    const videoConstraints: MediaTrackConstraints = {
      ...cfg,
      facingMode: this.currentFacing,
    };

    // En móvil intentamos anclar por deviceId para asegurar la cámara
    // exacta cuando hay varias. Sólo probamos esto en la primera pasada
    // (highEnd) — si falla caemos al facingMode del constraint.
    if (isMobile && configType === 'highEnd') {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter((d) => d.kind === 'videoinput');
        const wantBack = this.currentFacing === 'environment';
        const match = cameras.find((c) =>
          wantBack
            ? /back|rear|environment|trasera|posterior|0/i.test(c.label)
            : /front|user|selfie|frontal/i.test(c.label),
        );
        if (match?.deviceId) {
          videoConstraints.deviceId = { exact: match.deviceId };
        }
      } catch {
        // Fallback a facingMode.
      }
    }

    return { video: videoConstraints, audio: false };
  }

  /**
   * Aplica settings óptimos para captura de documentos:
   * enfoque continuo, exposición aumentada, balance de blancos automático.
   * Igual que el onboarding — algunos dispositivos ignoran silenciosamente.
   */
  private async applyOptimalSettings(stream: MediaStream): Promise<void> {
    try {
      const [track] = stream.getVideoTracks();
      const caps = track.getCapabilities() as any;
      const settings: MediaTrackConstraintSet[] = [];

      if (caps.focusMode?.includes('continuous')) {
        settings.push({ focusMode: 'continuous' } as any);
      } else if (caps.focusMode?.includes('auto')) {
        settings.push({ focusMode: 'auto' } as any);
      }

      if (caps.exposureMode?.includes('continuous')) {
        settings.push({ exposureMode: 'continuous' } as any);
      }

      if (caps.exposureCompensation) {
        const { min, max } = caps.exposureCompensation;
        const target = Math.min(0.5, max);
        if (target >= min) {
          settings.push({ exposureCompensation: target } as any);
        }
      }

      if (caps.whiteBalanceMode?.includes('continuous')) {
        settings.push({ whiteBalanceMode: 'continuous' } as any);
      }

      if (caps.zoom?.min) {
        settings.push({ zoom: caps.zoom.min } as any);
      }

      if (settings.length > 0) {
        await track.applyConstraints({ advanced: settings });
      }
    } catch {
      // No es crítico.
    }
  }

  // ── Captura ────────────────────────────────────────────────────────

  capture(): void {
    const video = this.videoElement?.nativeElement;
    if (!video || video.videoWidth === 0) {
      this.cameraError.emit('La cámara no está lista. Intentá de nuevo.');
      return;
    }
    this.loading = true;
    try {
      // Selfie: capturamos el video ENTERO sin recortar al óvalo — es
      // exactamente lo que hace el onboarding y garantiza que Rekognition
      // reciba una imagen con la misma geometría que la del signup.
      // Documento: recortamos al frame para que el OCR/Textract reciba
      // solo la cédula sin fondos ruidosos.
      this.currentImage =
        this.frameShape === 'face'
          ? this.captureFullFrame(video)
          : this.captureFrameArea(video);
      this.rotationAngle = 0;
      // Detenemos el stream ahora que ya tenemos la imagen — ahorra
      // batería / memoria mientras el usuario revisa el preview.
      this.stopStream();
    } catch {
      this.cameraError.emit('Error capturando la imagen. Intentá de nuevo.');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Captura el frame COMPLETO del video (sin recorte). Idéntico al
   * `captureSelfie()` del onboarding: canvas del tamaño del video,
   * `drawImage(video, 0, 0)` y compresión simple hasta el límite de
   * `maxFileSizeMB`. No aplica `reduceImageSize` — Rekognition prefiere
   * imágenes más grandes y sin re-escalados agresivos.
   */
  private captureFullFrame(video: HTMLVideoElement): string {
    const { videoWidth, videoHeight } = video;
    const canvas = document.createElement('canvas');
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo crear contexto de canvas');
    ctx.drawImage(video, 0, 0);
    return this.compressSimple(canvas);
  }

  /**
   * Compresión simple sin `reduceImageSize`. Baja la calidad hasta el
   * límite de `maxFileSizeMB` pero NO reescala el canvas — mantener las
   * dimensiones nativas evita que Rekognition rechace por tamaños raros
   * o artefactos de re-escalado.
   */
  private compressSimple(canvas: HTMLCanvasElement): string {
    let quality = CAPTURE_CONFIG.quality;
    let result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);
    while (
      this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB &&
      quality > CAPTURE_CONFIG.minQuality
    ) {
      quality -= CAPTURE_CONFIG.qualityStep;
      result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);
    }
    return result;
  }

  private stopStream(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  /**
   * Recorta el área del frame overlay (85% ancho, aspect ratio del
   * documento) y comprime a JPEG bajo `maxFileSizeMB`. Idéntico al
   * onboarding para que el backend reciba imágenes con las mismas
   * dimensiones y calidad.
   */
  private captureFrameArea(video: HTMLVideoElement): string {
    const { videoWidth, videoHeight } = video;
    const frameWidthPercent = 0.85;
    const frameWidth = Math.round(videoWidth * frameWidthPercent);
    const frameHeight = Math.round(frameWidth / this.frameAspectRatio);
    const startX = Math.round((videoWidth - frameWidth) / 2);
    const startY = Math.round((videoHeight - frameHeight) / 2);

    const canvas = document.createElement('canvas');
    let targetWidth = frameWidth;
    let targetHeight = frameHeight;
    if (targetWidth > CAPTURE_CONFIG.targetWidth) {
      const scale = CAPTURE_CONFIG.targetWidth / targetWidth;
      targetWidth = CAPTURE_CONFIG.targetWidth;
      targetHeight = Math.round(frameHeight * scale);
    }
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo crear contexto de canvas');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      video,
      startX,
      startY,
      frameWidth,
      frameHeight,
      0,
      0,
      targetWidth,
      targetHeight,
    );

    return this.compressToTargetSize(canvas);
  }

  private compressToTargetSize(canvas: HTMLCanvasElement): string {
    let quality = CAPTURE_CONFIG.quality;
    let result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);
    while (
      this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB &&
      quality > CAPTURE_CONFIG.minQuality
    ) {
      quality -= CAPTURE_CONFIG.qualityStep;
      result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);
    }
    if (this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB) {
      return this.reduceImageSize(canvas, quality);
    }
    return result;
  }

  private reduceImageSize(source: HTMLCanvasElement, quality: number): string {
    const scale = 0.75;
    const nw = Math.round(source.width * scale);
    const nh = Math.round(source.height * scale);
    const c = document.createElement('canvas');
    c.width = nw;
    c.height = nh;
    const ctx = c.getContext('2d');
    if (!ctx) return source.toDataURL(CAPTURE_CONFIG.format, quality);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, nw, nh);
    return c.toDataURL(CAPTURE_CONFIG.format, quality);
  }

  private getFileSizeMB(base64: string): number {
    const data = base64.split(',')[1] || '';
    return (data.length * 0.75) / (1024 * 1024);
  }

  // ── Rotación / retomar / confirmar ─────────────────────────────────

  rotateLeft(): void {
    this.rotationAngle = (this.rotationAngle - 90 + 360) % 360;
  }
  rotateRight(): void {
    this.rotationAngle = (this.rotationAngle + 90) % 360;
  }

  retake(): void {
    this.currentImage = '';
    this.rotationAngle = 0;
    // No reseteamos a `preferredFacing` — si el usuario alternó a la
    // otra cámara antes de disparar, respetamos su elección.
    this.openCamera(false);
  }

  async confirm(): Promise<void> {
    if (!this.currentImage) return;
    this.loading = true;
    try {
      const finalBase64 = await this.applyRotationAndOptimize(
        this.currentImage,
        this.rotationAngle,
      );
      this.captured.emit(finalBase64);
      this._isOpen = false;
      this.currentImage = '';
      this.rotationAngle = 0;
    } catch {
      this.cameraError.emit('Error procesando la imagen. Intentá de nuevo.');
    } finally {
      this.loading = false;
    }
  }

  private applyRotationAndOptimize(
    base64: string,
    angle: number,
  ): Promise<string> {
    if (angle === 0) return Promise.resolve(base64);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo crear contexto'));
          return;
        }
        if (angle % 180 === 0) {
          canvas.width = img.width;
          canvas.height = img.height;
        } else {
          canvas.width = img.height;
          canvas.height = img.width;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        // Para selfie usamos compresión simple (sin reduceImageSize)
        // por la misma razón que en `captureFullFrame`: preservar
        // dimensiones nativas para Rekognition.
        resolve(
          this.frameShape === 'face'
            ? this.compressSimple(canvas)
            : this.compressToTargetSize(canvas),
        );
      };
      img.onerror = () => reject(new Error('Error cargando imagen'));
      img.src = base64;
    });
  }
}
